import React, { FC, useCallback, useState } from "react";
import { MapViewModel } from "./MapViewModel.csharp";
import {
  GoogleMap,
  MarkerF,
  useLoadScript,
  InfoWindow,
  MarkerClustererF,
} from "@react-google-maps/api";
import { computeDistanceBetween } from "spherical-geometry-js";
import { Point } from "./Point.csharp";
import useWindowSize from "../../../Helpers/useWindowSize";
import { Button } from "../Insurance/Partials/Button";

const mapStyles = [
  {
    featureType: "administrative",
    elementType: "geometry",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "administrative.land_parcel",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "administrative.neighborhood",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "administrative.province",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "poi",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "poi",
    elementType: "labels.text",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "poi.attraction",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "poi.school",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "poi.sports_complex",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "road",
    elementType: "labels",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "road",
    elementType: "labels.icon",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "road.arterial",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "road.highway",
    elementType: "labels",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "road.local",
    stylers: [
      {
        visibility: "simplified",
      },
    ],
  },
  {
    featureType: "road.local",
    elementType: "geometry.fill",
    stylers: [
      {
        visibility: "simplified",
      },
    ],
  },
  {
    featureType: "transit",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "water",
    elementType: "labels.text",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
];

export const Map: FC<MapViewModel> = ({
  useBrandStyling,
  mapId,
  apiKey,
  markers,
  labelForClosestCenter,
}) => {
  const { isMobile } = useWindowSize();
  const [center, setCenter] = useState<Point>();
  const [activeMarkerId, setActiveMarkerId] = useState<number>();
  const [map, setMap] = useState<GoogleMap | null>(null);
  const [zoom, setZoom] = useState<number | undefined>(15);

  const markersMap = markers.map((marker, i) => {
    return { marker: marker, id: i };
  });

  const handleActiveMarker = (markerId: number) => {
    if (markerId === activeMarkerId) {
      return;
    }
    setActiveMarkerId(markerId);
  };

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey,
  });

  const onLoad = useCallback((map) => {
    setMap(map);
    if (markers.length > 1) {
      window.google.maps.DirectionsTravelMode;
      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach((marker) => {
        bounds.extend({ lat: marker.point.lat, lng: marker.point.lng });
      });
      map.fitBounds(bounds);
    } else {
      setCenter(markers[0].point);
    }
  }, []);

  const onFindClosestCenterClick = () => {
    navigator.geolocation.getCurrentPosition((position) => {
      const currentCoordinates = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };

      let minimalDistanceMarker = markersMap[0];
      let minimalDistance = computeDistanceBetween(
        currentCoordinates,
        minimalDistanceMarker.marker.point
      );

      for (const marker of markersMap) {
        const distance = computeDistanceBetween(
          currentCoordinates,
          marker.marker.point
        );

        if (distance < minimalDistance) {
          minimalDistance = distance;
          minimalDistanceMarker = marker;
        }
      }

      map!.panTo({
        lat: minimalDistanceMarker.marker.point.lat,
        lng: minimalDistanceMarker.marker.point.lng,
      });

      setZoom(13);
      setActiveMarkerId(minimalDistanceMarker.id);
    });
  };

  const brandStyling = useBrandStyling && !!mapId;
  
  return (
    <>
      {!!navigator.geolocation &&
        markers.length > 1 &&
        labelForClosestCenter && (
          <Button
            className="mt-2 p-1 find-center-btn"
            onClick={() => onFindClosestCenterClick()}
          >
            {labelForClosestCenter}
          </Button>
        )}
      {isLoaded && (
        <GoogleMap
          options={{
            streetViewControl: false,
            fullscreenControl: isMobile,
            mapTypeControl: !brandStyling,
            mapId: brandStyling ? mapId : null,
            styles: brandStyling ? null : mapStyles,
          }}
          onLoad={onLoad}
          mapContainerClassName="iframe"
          clickableIcons={false}
          zoom={zoom}
          center={center}
          onZoomChanged={() => setZoom(15)}
        >
          <MarkerClustererF
            styles={[
              {
                height: 35,
                url: "/Static/icons/volvat-map-cluster.png",
                width: 40,
              },
            ]}
          >
            {(clusterer) => (
              <>
                {markersMap.map((m) => (
                  <MarkerF
                    key={m.id}
                    position={{ ...m.marker.point }}
                    onClick={() => handleActiveMarker(m.id)}
                    clusterer={clusterer}
                    icon={{
                      scaledSize: new google.maps.Size(50, 50),
                      url: "/Static/icons/volvat-map-pin.png",
                    }}
                  >
                    {activeMarkerId === m.id ? (
                      <InfoWindow
                        onCloseClick={() => {
                          setActiveMarkerId(undefined);
                          setZoom(undefined);
                        }}
                      >
                        <div>
                          <h4 className="mt-0 fs-5 mb-1">
                            <a
                              href={m.marker.nameLink}
                              className="link-list--simple__item-link theme-border"
                              style={{ borderWidth: 2 }}
                            >
                              {m.marker.name}
                            </a>
                          </h4>
                          <p>
                            {m.marker.addressLine1}
                            <br />
                            {m.marker.addressLine2}
                          </p>
                          <a href={m.marker.detailsLink} target="_blank">
                            {m.marker.detailsLabel}
                          </a>
                        </div>
                      </InfoWindow>
                    ) : null}
                  </MarkerF>
                ))}
              </>
            )}
          </MarkerClustererF>
        </GoogleMap>
      )}
    </>
  );
};
